/* eslint-disable max-lines, max-lines-per-function */
import { type models } from "jiffy-api";
import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { pinningEnabledArtworksAtom } from "@design-lab/entities/pinning";
import { useProductColors } from "@design-lab/entities/product";
import { PRODUCT_ID, STANDARD_DTF_CATALOG_NUMBER } from "@design-lab/shared";
import { useDtfChannel } from "@design-lab/shared/hooks/useDtfChannel";
import {
  trackDesignUploadCompleted,
  trackDesignUploadFailed,
  trackDesignUploadStarted,
  trackImageDeleted
} from "@design-lab/shared/lib/analytics";
import { useExperiments } from "@design-lab/shared/lib/config";
import { getFileUrl, isPreviewBasedFile } from "@design-lab/shared/lib/file";
import { calculateImageDpi } from "@design-lab/shared/lib/imageUtils";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useDtfSideTracking } from "@design-lab/shared/hooks/useDtfSideTracking";
import api from "common/utils/api";
import { isApiError } from "@design-lab/shared/lib/errorGuards";
import { notifyError } from "common/utils/app-monitoring/notify";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import { generateUUID } from "common/utils/uuid";
import type { Artwork } from "@design-lab/shared/types/artwork";
import { useDftUploadArtwork } from "../api/useDftUploadArtwork";
import {
  clearArtworkFormValues,
  setArtworkDimensions,
  setArtworkId
} from "../lib/artworkFormHelpers";
import type { UploadArtworkParams } from "./types";
import { useArtwork } from "./useArtwork";
import { useArtworkForm } from "./useArtworkForm";
import { useImagesWarmFastCache } from "./useImagesFastCache";

/**
 * Provides functions for uploading, deleting, and marking artwork sides within the design lab.
 *
 * The returned utilities manage artwork lifecycle (uploading and enhancement flow, cache warming,
 * analytics tracking) and side state. `uploadArtwork` performs the upload and may throw on failure;
 * `deleteArtwork` removes artwork for a placement; `markSideAsDeleted` flags a side as deleted.
 *
 * @returns An object with:
 *  - `uploadArtwork`: uploads or processes an artwork file for a specified side and source (may throw on failure).
 *  - `deleteArtwork`: deletes artwork for a placement (`"front"` or `"back"`) and clears related form/machine state.
 *  - `markSideAsDeleted`: marks a side as deleted in side-tracking state.
 */
export function useArtworkImage() {
  const channel = useDtfChannel();
  const { markSideAsActive, markSideAsDeleted, isSideActive } = useDtfSideTracking();
  const { form } = useArtworkForm();
  const { currentComposition: composition } = useCurrentComposition();
  const { warmCache } = useImagesWarmFastCache();
  const setPinningEnabledArtworks = useSetAtom(pinningEnabledArtworksAtom);
  const { mutateAsync: uploadArtworkMutation } = useDftUploadArtwork();
  const { front, back, getMachineBySide } = useArtwork();
  const { currentProduct } = useProductColors();
  const experimentsConfig = useExperiments();

  const getBusinessLineAndOrderType = useCallback(() => {
    const catalogNumber = currentProduct?.catalogNumber || STANDARD_DTF_CATALOG_NUMBER;

    // Design lab is always used for print-on-demand services
    const businessLine = "Print";
    const orderType = "print";

    return { businessLine, orderType, catalogNumber };
  }, [currentProduct]);

  const handlePreEnhancedArtwork = useCallback(
    async ({
      response,
      side,
      file,
      source,
      sourceComposition,
      uploadStartTime,
      uploadUuid
    }: {
      response: models.dtf.uploadArtwork.Response;
      side: "front" | "back";
      file: File;
      source: "upload" | "design_library" | "canva" | "ai_generation" | "camera" | undefined;
      sourceComposition: models.printOnDemand.getProduct.Composition | undefined;
      uploadStartTime: number;
      uploadUuid: string;
    }) => {
      const machine = side === "front" ? front : back;
      const {
        businessLine: completionBusinessLine,
        orderType: completionOrderType,
        catalogNumber: completionCatalogNumber
      } = getBusinessLineAndOrderType();

      const completionSurface = sourceComposition?.surfaces?.[side];
      const completionDpi = await calculateImageDpi(file, completionSurface);
      const id = response.id.toString();

      trackDesignUploadCompleted(side, id, source || "upload", {
        file,
        catalogNumber: completionCatalogNumber,
        businessLine: completionBusinessLine,
        orderType: completionOrderType,
        page: "design_lab",
        pdpType: completionOrderType,
        projectId: null,
        multiArtwork: false,
        artworksCount: 1,
        dpi: completionDpi,
        preCutTransfers: false,
        uploadStartTime,
        uploadUuid
      });

      setArtworkId(form, side, id);

      const pinningEnabled = (response as { pinningEnabled?: boolean }).pinningEnabled ?? true;
      setPinningEnabledArtworks(prev => ({ ...prev, [id]: pinningEnabled }));

      machine.send({ type: "enhancement.started" });

      machine.send({ type: "cache.warming.started" });
      await warmCache([response.noBgUrl || response.withBgUrl]);
      machine.send({ type: "cache.warmed" });

      const artwork = {
        id: response.id,
        originalImageUrl: response.originalImageUrl,
        url: response.noBgUrl || response.url,
        withBgUrl: response.withBgUrl,
        noBgUrl: response.noBgUrl,
        noBgNoTrimUrl: response.noBgNoTrimUrl,
        manualEditUrl: response.manualEditUrl || null,
        manualEditNoTrimUrl: response.manualEditNoTrimUrl || null,
        canRemoveBg: response.canRemoveBg ?? true,
        removeBackground: response.removeBackground ?? false,
        enhanceImage: response.enhanceImage ?? true,
        metadata: response.metadata
      } as unknown as Artwork;

      machine.send({
        type: "enhancement.completed",
        artwork,
        triggerEvent: "plp_upload"
      });
    },
    [form, getBusinessLineAndOrderType, warmCache, setPinningEnabledArtworks]
  );

  const uploadArtwork = async ({
    file,
    side,
    source,
    existingArtworkId,
    sourceComposition = composition,
    aiImageVersionId,
    experiments = JSON.stringify(experimentsConfig),
    uploadSource = "design-lab"
  }: UploadArtworkParams) => {
    const uploadStartTime = performance.now();

    const { businessLine, orderType, catalogNumber } = getBusinessLineAndOrderType();

    const surface = sourceComposition?.surfaces?.[side];
    const dpi = await calculateImageDpi(file, surface);

    const uploadUuid = generateUUID();

    trackDesignUploadStarted(side, source || "upload", {
      file,
      catalogNumber,
      businessLine,
      orderType,
      page: "design_lab",
      pdpType: orderType,
      projectId: null,
      multiArtwork: false,
      artworksCount: 1,
      dpi,
      preCutTransfers: false,
      uploadStartTime,
      uploadUuid
    });

    const dtfChannelUuid = await channel.retrieveDtfChannelUuid();
    const machine = side === "front" ? front : back;

    // If machine is in finished state, reset it to initial for new upload
    // This happens during auto-upload when the machine was restored with existing artwork
    if (machine.state.matches("finished")) {
      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          visitedStates: [],
          cacheWarmingStatus: "none"
        }
      });
    }

    if (!dtfChannelUuid) {
      const errorMessage = "DTF channel UUID is not available";

      machine.send({
        type: "reset",
        context: {
          artworkId: existingArtworkId ? parseInt(existingArtworkId, 10) : undefined,
          visitedStates: [],
          cacheWarmingStatus: "none"
        }
      });

      throw new Error(errorMessage);
    }

    const url = isPreviewBasedFile(file) ? await getFileUrl(file) : undefined;
    const [cachedImage] = await warmCache([url]);

    setArtworkDimensions({
      form,
      side,
      cachedImage,
      surfaces: sourceComposition?.surfaces
    });

    const processTimeStamp = Date.now();

    markSideAsActive(side, String(processTimeStamp));

    const fields = {
      file,
      productId: PRODUCT_ID,
      fileOriginalIndex: "0",
      dtfChannelUuid,
      preCut: "false",
      processTimeStamp: String(processTimeStamp),
      isMade: true,
      existingArtworkId: existingArtworkId || "",
      aiImageVersionId: aiImageVersionId || "",
      uploadSource,
      experiments
    } as models.dtf.uploadArtwork.Request;

    machine.send({
      type: "upload.started",
      file,
      source: source || "upload",
      processTimeStamp: String(processTimeStamp)
    });

    let response;
    try {
      response = objNamesToCamelCase(await uploadArtworkMutation(fields));
    } catch (error) {
      const errorData = isApiError(error) ? error.response?.data : undefined;
      const isTimeoutError =
        errorData?.error_type === "timeout_error" ||
        (isApiError(error) && error.response?.status === 504);
      const isValidationError =
        errorData?.error_type === "backend_file_validation" || errorData?.validation_error;
      const errorMessage = errorData?.validation_error || errorData?.error || "Unknown error";

      const getErrorType = () => {
        if (isTimeoutError) return "timeout_error";
        if (isValidationError) return "validation_error";
        return "unhandled_error";
      };

      trackDesignUploadFailed({
        type: getErrorType(),
        errorMessage
      });

      machine.send({
        type: "reset",
        targetState: "initial",
        context: {
          artworkId: existingArtworkId ? parseInt(existingArtworkId, 10) : undefined,
          visitedStates: [],
          cacheWarmingStatus: "none"
        }
      });
      throw error;
    }

    // Check if artwork is already enhanced from lightweight pipeline
    const isPreEnhanced =
      response.lightWeightPipeline === true && response.enhancementStatus === "processed";

    machine.send({
      type: "upload.completed",
      id: Number(response.id)
    });

    machine.send({
      type: "set.originalImageUrl",
      originalImageUrl: response.originalImageUrl
    });

    if (!isSideActive(side, String(processTimeStamp))) {
      return;
    }

    if (isPreEnhanced) {
      await handlePreEnhancedArtwork({
        response,
        side,
        file,
        source,
        sourceComposition,
        uploadStartTime,
        uploadUuid
      });
      return;
    }

    const {
      businessLine: completionBusinessLine,
      orderType: completionOrderType,
      catalogNumber: completionCatalogNumber
    } = getBusinessLineAndOrderType();

    const completionSurface = sourceComposition?.surfaces?.[side];
    const completionDpi = await calculateImageDpi(file, completionSurface);
    const id = response.id.toString();

    trackDesignUploadCompleted(side, id, source || "upload", {
      file,
      catalogNumber: completionCatalogNumber,
      businessLine: completionBusinessLine,
      orderType: completionOrderType,
      page: "design_lab",
      pdpType: completionOrderType,
      projectId: null,
      multiArtwork: false,
      artworksCount: 1,
      dpi: completionDpi,
      preCutTransfers: false,
      uploadStartTime,
      uploadUuid
    });

    setArtworkId(form, side, id);

    const pinningEnabled = (response as { pinningEnabled?: boolean }).pinningEnabled ?? true;
    setPinningEnabledArtworks(prev => ({ ...prev, [id]: pinningEnabled }));
  };

  const cancelArtworkProcess = useCallback(async (artworkId: number) => {
    try {
      await api.dtf.cancelArtworkProcess({ id: artworkId }, { catalog_number: "POD001" });
    } catch (error) {
      notifyError("cancelArtworkProcess failed", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          artworkId,
          location: "design-lab"
        }
      });
    }
  }, []);

  const deleteArtwork = useCallback(
    async (placement: string, artworkId?: string) => {
      const side = placement === "back" ? "back" : "front";
      const machine = getMachineBySide(side);

      // If enhancement is in progress and we have an artworkId, cancel the process
      const isEnhancementInProgress = machine.state.hasTag("loading");
      if (isEnhancementInProgress && artworkId) {
        cancelArtworkProcess(Number(artworkId));
      }

      markSideAsDeleted(side);
      machine.send({ type: "reset" });
      trackImageDeleted(side, artworkId);
      clearArtworkFormValues(form, placement);
    },
    [form, getMachineBySide, markSideAsDeleted, cancelArtworkProcess]
  );

  return {
    uploadArtwork,
    deleteArtwork,
    markSideAsDeleted
  };
}
